import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { faDigits } from '../../lib/numerals'
import { badgeName, freezeState, gemsOn } from '../../lib/shop'

/** The gem (V6-b-gem): drawn only while the server's gem economy is on. */
export function GemGlyph({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.5 3.5h11L22 9l-10 11.5L2 9z" fill="currentColor" />
      <path d="M2 9h20M9 3.5L12 20.5 15 3.5" fill="none" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  )
}

function ShieldGlyph({ size = 19 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7 2.6v5.6c0 4.3-2.9 7.6-7 8.8-4.1-1.2-7-4.5-7-8.8V5.6z" />
    </svg>
  )
}

function MedalGlyph({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="14.5" r="5.5" />
      <path d="M8.5 9.8L6 3h4l2 4.5L14 3h4l-2.5 6.8" />
    </svg>
  )
}

/**
 * The streak-freeze card (V6-w): the freezes held against the server's cap,
 * `x/max`, one dot per slot, and the door to the shop while there is room.
 * Renders nothing unless the server's gem economy is on (`gemsEnabled`,
 * off on production until stage 3) — so the profile looks as it did.
 */
export function FreezeCard({ wallet }) {
  const { t } = useTranslation('shop')
  const freezes = freezeState(wallet)
  if (!freezes) return null
  return (
    <section className="space-y-2.5 card-soft p-3.5" data-freeze-card={`${freezes.held}/${freezes.max}`}>
      <div className="flex items-center gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-700">
          <ShieldGlyph />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[13.5px] font-bold text-ink">{t('freezeCard.title')}</span>
          <span className="tnum text-[12px] font-semibold text-brand-700">{t('page.freezeCount', { held: faDigits(freezes.held), max: faDigits(freezes.max) })}</span>
        </span>
        <span className="flex gap-1" aria-hidden="true">
          {Array.from({ length: freezes.max }, (_, index) => (
            <span key={index} className={`size-2.5 rounded-full ${index < freezes.held ? 'bg-brand-600' : 'bg-track'}`} />
          ))}
        </span>
      </div>
      <p className="text-[11.5px] leading-[1.85] text-ink-2">{freezes.full ? t('page.freezeFullHint') : t('freezeCard.body')}</p>
      {!freezes.full && (
        <Link to="/shop" className="flex h-9 items-center justify-center rounded-[10px] bg-brand-50 text-[12.5px] font-bold text-brand-700 transition hover:bg-surface-2">
          {t('freezeCard.buy')}
        </Link>
      )}
    </section>
  )
}

/**
 * The learner's badges (V6-w) from `WalletDto.badges`, newest first as the
 * server sends them, with an empty state until period settlement awards
 * one. Renders nothing unless the gem economy is on — badges ship with it.
 */
export function BadgeGrid({ wallet }) {
  const { t } = useTranslation('shop')
  if (!gemsOn(wallet)) return null
  const badges = Array.isArray(wallet.badges) ? wallet.badges : []
  return (
    <section className="space-y-2.5 card-soft p-3.5" data-badges={badges.length}>
      <h2 className="text-[13.5px] font-bold text-ink">{t('badges.heading')}</h2>
      {badges.length === 0 ? (
        <p className="text-[11.5px] leading-[1.85] text-ink-2">{t('badges.empty')}</p>
      ) : (
        <ul className="grid grid-cols-3 gap-2">
          {badges.map((badge) => (
            <li key={`${badge.key}:${badge.periodKey}`} className="flex flex-col items-center gap-1.5 rounded-xl bg-accent-tint p-2.5 text-center text-accent-ink">
              <MedalGlyph />
              <span className="text-[11px] font-bold leading-snug">{badgeName(badge)}</span>
              <span className="ltr tnum text-[10px] opacity-75">{badge.periodKey}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
